import React from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { formatDate } from '../utils/timeFormat';

// Sorting happens on the server (the lists are paged), so a header click
// asks the page for a new sort instead of reordering rows here. Styled to
// match CandidatesTable and the other list tables.

function cellValue(row, column) {
  if (column.render) return column.render(row);
  const value = row[column.key];
  if (column.type === 'date') return formatDate(value);
  if (column.type === 'number') return value ?? 0;
  if (value === null || value === undefined || value === '') return '-';
  return value;
}

const AdminActivityTable = ({
  columns,
  rows,
  sort,
  order,
  onSort,
  onRowClick,
  label,
}) => {
  if (!rows.length) return <></>;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ overflow: 'visible' }}
    >
      <Table sx={{ minWidth: 650 }} aria-label={label}>
        <TableHead>
          <TableRow sx={{ '.MuiTableCell-root': { padding: '16px 8px' } }}>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                align={column.type === 'number' ? 'right' : 'left'}
                sx={{
                  fontWeight: sort === column.key ? 'bold' : 'normal',
                  cursor: column.sortable === false ? 'default' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onClick={() =>
                  column.sortable !== false && onSort(column.key, column.type)
                }
              >
                {column.label}
                {sort === column.key &&
                  (order === 'asc' ? (
                    <ArrowUpward
                      fontSize="small"
                      sx={{ verticalAlign: 'top' }}
                    />
                  ) : (
                    <ArrowDownward
                      fontSize="small"
                      sx={{ verticalAlign: 'top' }}
                    />
                  ))}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              sx={{
                cursor: onRowClick ? 'pointer' : 'default',
                '.MuiTableCell-root': { padding: 1 },
                ':hover': { backgroundColor: 'info.main' },
              }}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.type === 'number' ? 'right' : 'left'}
                  sx={column.type === 'date' ? { whiteSpace: 'nowrap' } : {}}
                >
                  {cellValue(row, column)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AdminActivityTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['string', 'number', 'date']),
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  sort: PropTypes.string,
  order: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func.isRequired,
  onRowClick: PropTypes.func,
  label: PropTypes.string,
};

export default AdminActivityTable;
